import React, { useState, useEffect } from "react";
import { DialogBox } from "../../../../../components/Elements/DialogBox";
import { Button, Input, Modal } from "antd";
import { API_URLS } from "../../../../../api/apiUrls";
import { putApi } from "../../../../../api/api_instance";
import { AcceptDialog } from "../../../../../components/Elements/DialogBox/AcceptDialog";
import CustomSelect from "../../../../../components/Elements/Select/CustomSelect";
import { THEME_COLORS } from "../../../../../color";
import PopupCard from "../../../../Auth/PopupCard";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchAllDepartments,
  fetchAllLocations,
  fetchLinkedItems,
} from "../../../../../redux/OrganizationSetup/organizationSetupSlice";

const EditUsersDialog = ({ onOpen, onClose, userData, onUpdate }) => {
  const [name, setName] = useState("");
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("");
  const [location, setLocation] = useState("");
  const [department, setDepartment] = useState("");
  const [loading, setLoading] = useState(false);
  const [showAcceptDialog, setShowAcceptDialog] = useState(false);
  const [showPopup, setShowPopup] = useState(false);
  const [popupData, setPopupData] = useState({
    type: "success",
    title: "Success",
    message: "",
  });

  const [errorMessage, setErrorMessage] = useState({
    name: "",
    username: "",
    email: "",
    role: "",
    location: "",
    department: "",
  });

  const dispatch = useDispatch();
  const {
    orgMode: setupOrgMode,
    locationList,
    departmentList,
    linkedItems,
  } = useSelector((state) => state.orgSetup);
  const authUser = useSelector((state) => state.auth.user);
  const orgMode = setupOrgMode || authUser?.orgStructureMode;

  const triggerPopup = (type, title, message, duration = 3000) => {
    setPopupData({ type, title, message });
    setShowPopup(true);
    setTimeout(() => setShowPopup(false), duration);
  };

  useEffect(() => {
    if (userData) {
      setName(userData.name || "");
      setUsername(userData.username || "");
      setRole(userData.role || "");
      setEmail(userData.email || "");
      setLocation(
        userData.locationId ||
          userData.location?._id ||
          userData.location ||
          "",
      );
      setDepartment(
        userData.departmentId ||
          userData.department?._id ||
          userData.department ||
          "",
      );
    }
  }, [userData]);

  useEffect(() => {
    dispatch(fetchAllLocations());
    dispatch(fetchAllDepartments());
  }, [dispatch]);

  useEffect(() => {
    if (orgMode === "department-first") {
      if (department) {
        dispatch(fetchLinkedItems(department));
      }
      return;
    }

    if (orgMode === "location-first" && location) {
      dispatch(fetchLinkedItems(location));
    }
  }, [dispatch, orgMode, department, location]);

  const handleDeptChange = (val) => {
    setDepartment(val);
    setLocation("");
    if (orgMode === "department-first") {
      dispatch(fetchLinkedItems(val));
    }
  };

  const handleLocChange = (val) => {
    setLocation(val);
    setDepartment("");
    if (orgMode === "location-first") {
      dispatch(fetchLinkedItems(val));
    }
  };

  const departmentOptions = (
    (orgMode === "department-first"
      ? departmentList
      : orgMode === "location-first"
        ? linkedItems
        : departmentList) || []
  )
    .filter(Boolean)
    .map((dept) => ({
      label: dept?.department_name || dept?.name || "Unnamed Department",
      value: dept?._id,
    }));

  const locationOptions = (
    (orgMode === "location-first"
      ? locationList
      : orgMode === "department-first"
        ? linkedItems
        : locationList) || []
  )
    .filter(Boolean)
    .map((loc) => ({
      label: loc?.location_name || loc?.name || "Unnamed Location",
      value: loc?._id,
    }));

  const roleOptions = [
    { label: "Employee", value: "employee" },
    { label: "Vendor", value: "vendor" },
    { label: "User", value: "user" },
  ];

  const handleSave = async () => {
    let isValid = true;
    const errors = { name: "", email: "", role: "" };

    if (!name) {
      errors.name = "Name is required";
      isValid = false;
    }
    if (!email) {
      errors.email = "Email is required";
      isValid = false;
    }
    if (!role) {
      errors.role = "Role is required";
      isValid = false;
    }

    setErrorMessage(errors);
    if (!isValid) return;

    if (!userData || !userData._id) {
      triggerPopup("error", "Invalid user data.", "Invalid user data");
      return;
    }

    setLoading(true);

    try {
      const response = await putApi({
        apiUrl: API_URLS.UPDATE_EMPLOYEE(userData._id),
        data: {
          name,
          email,
          role: Array.isArray(role) ? role : [role],
          status: "Active",
          location,
          locationId: location || null,
          department,
          departmentId: department || null,
        },
      });

      if (response.success) {
        triggerPopup("success", "Success", "User updated successfully!");
        setTimeout(() => {
          Modal.destroyAll();
          onUpdate();
          onClose();
        }, 2000);
      } else {
        triggerPopup(
          "error",
          "Update failed",
          response.message || "Update failed",
        );
      }
    } catch (error) {
      triggerPopup(
        "error",
        "Update failed",
        error.response?.data?.message || "Update failed",
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <DialogBox open={onOpen} onCancel={onClose} width="90%" maxWidth="600px">
        <div>
          <h3
            className="text-xl font-bold"
            style={{ color: THEME_COLORS.green }}
          >
            Edit User
          </h3>
        </div>

        <div className="rounded-xl border mt-8 px-12 pt-4">
          <label className="block text-sm font-semibold text-[#000000] mb-1 font-calibri">
            Name
          </label>
          <div className="flex flex-col gap-6">
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Name"
              className="h-14 rounded-lg"
            />

            {errorMessage.name && (
              <div className="text-red-500 text-sm">{errorMessage.name}</div>
            )}
            <div>
              <label className="block text-sm font-semibold text-[#000000] font-calibri">
                Email
              </label>
              <Input
                value={email}
                disabled
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Email"
                className="h-14 rounded-lg"
              />
              {errorMessage.email && (
                <div className="text-red-500 text-sm">{errorMessage.email}</div>
              )}
            </div>
            <div>
              <label className="block text-sm font-semibold text-[#000000] mb-1 font-calibri">
                Role
              </label>
              <CustomSelect
                options={roleOptions}
                mode="multiple"
                height="40px"
                placeholder="Select Role(s)"
                value={role}
                onChange={setRole}
              />
              {errorMessage.role && (
                <div className="text-red-500 text-sm">{errorMessage.role}</div>
              )}
            </div>

            {orgMode === "department-first" ? (
              <>
                <div>
                  <label className="block text-sm font-semibold text-[#000000] mb-1 font-calibri">
                    Department
                  </label>
                  <CustomSelect
                    options={departmentOptions}
                    height="40px"
                    placeholder="Select Department"
                    value={department}
                    onChange={handleDeptChange}
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-[#000000] mb-1 font-calibri">
                    Location
                  </label>
                  <CustomSelect
                    options={locationOptions}
                    height="40px"
                    placeholder="Select Location"
                    value={location}
                    onChange={(val) => setLocation(val)}
                    disabled={!department}
                  />
                </div>
              </>
            ) : (
              <>
                <div>
                  <label className="block text-sm font-semibold text-[#000000] mb-1 font-calibri">
                    Location
                  </label>
                  <CustomSelect
                    options={locationOptions}
                    height="40px"
                    placeholder="Select Location"
                    value={location}
                    onChange={handleLocChange}
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-[#000000] mb-1 font-calibri">
                    Department
                  </label>
                  <CustomSelect
                    options={departmentOptions}
                    height="40px"
                    placeholder="Select Department"
                    value={department}
                    onChange={(val) => setDepartment(val)}
                    disabled={!location}
                  />
                </div>
              </>
            )}
          </div>

          <div className="flex justify-center gap-6 p-5">
            <Button
              onClick={onClose}
              style={{
                background: "#E6EEEC",
                height: "40px",
                width: "120px",
                borderRadius: "10px",
                border: "1px solid #06513D",
                color: "#E6EDEB",
              }}
              className="text-base font-normal !text-[#06513D] hover:!bg-[#F8F8F8] hover:!border-[#06513D] "
            >
              Cancel
            </Button>
            <Button
              loading={loading}
              onClick={handleSave}
              style={{
                background: "#06513D",
                color: "white",
                height: "40px",
                width: "120px",
                borderRadius: "10px",
              }}
              className="text-base font-normal border-none hover:!bg-[#06513D] "
            >
              Save
            </Button>
          </div>

          {showPopup && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
              <PopupCard
                type={popupData.type}
                title={popupData.title}
                message={popupData.message}
              />
            </div>
          )}
        </div>
      </DialogBox>

      {showAcceptDialog && (
        <AcceptDialog
          open={showAcceptDialog}
          close={() => {
            setShowAcceptDialog(false);
            onClose();
          }}
          heading="User Updated"
          subheading="The user details have been successfully updated."
          success={true}
        />
      )}
    </>
  );
};

export default EditUsersDialog;
